import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Search } from "lucide-react";
import { Spinner } from "@/view/components/ui/spinner";
import { useManageCustomerController } from "./useManageCustomersController";
import { CustomerSortDropdown } from "./components/CustomerSortDropdown";
import { CustomerCard, extractCustomerCards } from "./components/CustomerCard";

export const Radar = () => {
  const {
    tabs,
    activeTab,
    setActiveTab,
    TabType,
    data,
    handleCustomerDetailsPage,
    handleSearchInputChange,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    ref,
    searchValue,
    openWhatsApp,
    handleCustomerStatus,
    contactedCustomers,
    sort,
    handleSortChange,
    sortIcon,
    renderNoCustomers,
    handleProposal,
    productsCatalog,
  } = useManageCustomerController();

  return (
    <div className="flex gap-5">
      <div className="w-full">
        <h2 className="mb-8 text-2xl font-semibold">Clientes</h2>
        <div className="flex gap-6">
          {tabs.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`pb-1 text-sm font-bold transition-colors duration-200 ${
                activeTab === id
                  ? "border-primaryHex border-b-2 text-black"
                  : "border-b-2 border-transparent text-gray-400 hover:text-black"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative mt-4 flex w-full items-center">
          <Search size={20} className="z-2 absolute left-4 top-3" />
          <Input
            className="w-full rounded-lg pl-12"
            placeholder="Pesquisar"
            onChange={handleSearchInputChange}
            value={searchValue}
          />
          <CustomerSortDropdown
            sort={sort}
            sortIcon={sortIcon}
            onSortChange={handleSortChange}
          />
        </div>
        {activeTab === TabType.WithBalance && (
          <div className="mt-4 flex flex-wrap gap-4">
            {!isFetching && !isFetchingNextPage && renderNoCustomers()
              ? renderNoCustomers()
              : data?.pages.map((page) =>
                  page.customers.flatMap((customer) =>
                    extractCustomerCards(customer)
                      .filter(
                        (cardData) =>
                          cardData.available?.amount &&
                          cardData.available?.amount > 0 &&
                          cardData.available?.isAutorized === true,
                      )
                      .map((cardData) => (
                        <CustomerCard
                          key={`${cardData._id}-${cardData.type}`}
                          customer={cardData}
                          productsCatalog={productsCatalog}
                          contacted={contactedCustomers.has(cardData._id)}
                          onClick={() =>
                            handleCustomerDetailsPage(cardData._id)
                          }
                          onContactClick={() =>
                            openWhatsApp({
                              _id: cardData._id,
                              phone: cardData.phone,
                            })
                          }
                          onProposeClick={() =>
                            handleProposal({
                              id: cardData._id,
                              cpf: cardData.cpf || "",
                              zipCode: cardData.zipCode,
                              type: cardData.type,
                            })
                          }
                          onArchiveClick={() =>
                            handleCustomerStatus({
                              id: cardData._id,
                              action: "archive",
                            })
                          }
                          variant="withBalance"
                        />
                      )),
                  ),
                )}
          </div>
        )}

        {activeTab === TabType.Unauthorized && (
          <div className="mt-4 flex flex-wrap gap-4">
            {!isFetching && !isFetchingNextPage && renderNoCustomers()
              ? renderNoCustomers()
              : data?.pages.map((page) =>
                  page.customers.flatMap((customer) =>
                    extractCustomerCards(customer)
                      .filter((cardData) => !cardData.available?.isAutorized)
                      .map((cardData) => (
                        <CustomerCard
                          key={`${cardData._id}-${cardData.type}`}
                          customer={cardData}
                          contacted={contactedCustomers.has(cardData._id)}
                          onClick={() =>
                            handleCustomerDetailsPage(cardData._id)
                          }
                          onContactClick={() =>
                            openWhatsApp({
                              _id: cardData._id,
                              phone: cardData.phone,
                            })
                          }
                          onArchiveClick={() =>
                            handleCustomerStatus({
                              id: cardData._id,
                              action: "archive",
                            })
                          }
                          variant="unauthorized"
                        />
                      )),
                  ),
                )}
          </div>
        )}

        {activeTab === TabType.Archived && (
          <div className="mt-4 flex flex-wrap gap-4">
            {!isFetching && !isFetchingNextPage && renderNoCustomers()
              ? renderNoCustomers()
              : data?.pages.map((page) =>
                  page.customers.flatMap((customer) =>
                    extractCustomerCards(customer).map((cardData) => (
                      <CustomerCard
                        key={`${cardData._id}-${cardData.type}`}
                        customer={cardData}
                        contacted={false}
                        onClick={() => handleCustomerDetailsPage(cardData._id)}
                        onContactClick={() =>
                          openWhatsApp({
                            _id: cardData._id,
                            phone: cardData.phone,
                          })
                        }
                        onUnarchiveClick={() =>
                          handleCustomerStatus({
                            id: cardData._id,
                            action: "unarchive",
                          })
                        }
                        variant="archived"
                      />
                    )),
                  ),
                )}
          </div>
        )}
        <div className="mt-2 flex items-center justify-center" ref={ref}>
          <Button variant="ghost" disabled={!hasNextPage || isFetchingNextPage}>
            {isFetchingNextPage ? <Spinner /> : null}
          </Button>
        </div>
        <div>
          {isFetching && !isFetchingNextPage ? (
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
